import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import iso3166 from "iso-3166-2";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory } from "react-router";

import {
  Button,
  Icon,
  Link,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetAllRelationships,
  useGetOwnerDetails,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  type Bank,
  createBank,
  uploadBankStatement,
} from "~/v2/api/rest/account";
import { FullScreenSpinner } from "~/v2/components/common";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { getISO3166Alpha3FormatMapping } from "~/v2/utils/formatting";
import { FAQ, type FAQItem } from "../../../../../components/common/FAQ";
import { BankLinkageAlert } from "../../common/BankLinkageAlert";
import { Layout } from "../../common/Layout";
import { InternationalWireNotes } from "../../deposit/wire/InternationalWireNotes";
import { useGetPaths } from "../useGetPath";
import { type WireSchema, zWireSchema } from "./schema";
import { WireFormFields } from "./WireFormFields";

export const WIRE_FAQ: FAQItem[] = [
  {
    question: "Does Alpaca charge for wire transfers?",
    answer: (
      <>
        Incoming wires are free. Outgoing wire fees are $25 for U.S. bank
        transfers and $50 for Non-U.S. bank transfers.
        <br />
        <br />
        Please see the{" "}
        <a
          href="https://files.alpaca.markets/disclosures/library/BrokFeeSched.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Fee Schedule
        </a>
        .
      </>
    ),
  },
  {
    question: "How long does it take to deposit in my account?",
    answer: (
      <>
        U.S. bank wires typically take 2–3 business days, depending on your
        bank.
        <br />
        <br />
        Non-U.S. bank wires can take up to 7 business days, depending on the
        intermediary bank used by your bank.
      </>
    ),
  },
  {
    question: "Can I transfer funds from another person into my account?",
    answer: (
      <>
        Only transfers made from an account in the same name as the Alpaca
        client are supported. Third-party transfers are not accepted.
      </>
    ),
  },
  {
    question: "What is the withdrawal limit?",
    answer: (
      <>
        We have a withdrawal limit of $50,000 per transfer. If you would like to
        transfer an amount exceeding this limit, please email{" "}
        <Link href="mailto:support@alpaca.markets">support@alpaca.markets</Link>
      </>
    ),
  },
  {
    question:
      "What should I do if my wire transfer has not been posted to my Alpaca account?",
    answer: (
      <div>
        Common reasons for delays or issues with international wires include:
        <ul className="my-4 list-outside list-disc pl-6">
          <li>Missing Memo Instructions</li>
          <li>Country Restrictions</li>
          <li>Intermediary Routing Delays</li>
        </ul>
        If your wire deposit has not been posted within 7 business days, please
        email{" "}
        <Link href="mailto:support@alpaca.markets">support@alpaca.markets</Link>{" "}
        with a PDF wire confirmation document.
      </div>
    ),
  },
  {
    question: "Is my bank supported for international wire transfers?",
    answer: (
      <>
        Neither the intermediary nor your bank should be on our restricted
        country list.
      </>
    ),
  },
  {
    question: "What else do I need to know for international wire transfers?",
    answer: (
      <>
        Most international banks use an intermediary or correspondent bank. For
        outgoing wires, we require details for both the intermediary bank and
        the FFC (For Further Credit) instructions for your bank and account.
        <br />
        <br />
        Some countries or banks require or recommend an IBAN for successful wire
        transfers.{" "}
        <a
          href="https://www.iban.com/iban-mandatory"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Check region-specific requirements
        </a>
        .
      </>
    ),
  },
];

const LinkWirePage = () => {
  const history = useHistory();
  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const { wire } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });
  const isBankLinkageDisallowed =
    currentAccount?.capabilities?.bank_linkable_status === "disallowed";

  const form = useForm<WireSchema>({
    mode: "onChange",
    resolver: zodResolver(zWireSchema),
    defaultValues: {
      bank_code_type: "ABA",
    },
  });

  const paths = useGetPaths("Wire");

  const createWireConnection = useMutationRequest(
    ({ bank, bankStatement }: { bank: Bank; bankStatement: File }) =>
      Promise.all([
        uploadBankStatement({
          accountID: currentAccount?.id || "",
          file: bankStatement,
        }),
        createBank({
          accountID: currentAccount?.id || "",
          bank,
        }),
      ]),
    {
      onSuccess: () => {
        toastSuccess("Bank linked successfully");
        wire.refetch();
        history.push(paths.next);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to link bank: ${errorMessage}`, {
          autoClose: false,
        });
      },
    },
  );

  const onBackClicked = () => {
    history.push(paths.back);
  };

  const onSubmitValid: SubmitHandler<WireSchema> = (data) => {
    const { isInternational, bank_statement, ...bank } = data;
    createWireConnection.mutate({
      bank: {
        ...(bank as Bank),
        bank_code_type: isInternational ? "BIC" : "ABA",
      },
      bankStatement: bank_statement,
    });
  };

  const onSubmitInvalid: SubmitErrorHandler<WireSchema> = (_error) => {
    toastError("Invalid form. Please check the form for errors.");
  };

  const isInternational = form.watch("isInternational");

  const countryList = useMemo(() => {
    const iso3166Alpha3FormatMapping = getISO3166Alpha3FormatMapping();
    return Object.keys(iso3166.data)
      .map((code) => {
        return {
          key: iso3166Alpha3FormatMapping[code],
          label: iso3166.data[code].name,
        };
      })
      .filter((value) => value.key !== "USA")
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: window.location.search is not reactive, so this dep can never fire; it documents that the value is read from the URL at mount. Reading useLocation() instead would make it reactive and is left for a follow-up.
  useEffect(() => {
    const params = new URLSearchParams(window?.location?.search ?? "");
    const isInternational = params.get("international") === "true";
    form.setValue("isInternational", isInternational);
  }, [window?.location?.search, form.setValue]);

  if (!currentAccount || !details) {
    return <FullScreenSpinner />;
  }

  return (
    <Layout
      title="Link Bank for Wire Transfers"
      subtitle="Link a bank account to withdraw funds via wire transfer."
      className="p-2"
      faq={<FAQ faq={WIRE_FAQ} />}
    >
      <Button onClick={onBackClicked} variant="secondary" className="mb-8">
        <Icon name="ArrowLeftCircle" className="mr-2" /> Back
      </Button>

      {isBankLinkageDisallowed ? (
        <BankLinkageAlert />
      ) : (
        <form
          onSubmit={form.handleSubmit(onSubmitValid, onSubmitInvalid)}
          className="w-full max-w-lg rounded border border-gray-200 p-4"
        >
          <Text className="pb-4 font-semibold">Bank Information</Text>
          {isInternational && <InternationalWireNotes className="mb-8" />}
          <WireFormFields form={form} countryList={countryList} />
          <div className="pt-4">
            <Button
              type="submit"
              disabled={createWireConnection.isPending}
              className="w-full"
            >
              <Icon name="Link" className="mr-2" />
              Link Bank
            </Button>
          </div>
        </form>
      )}
    </Layout>
  );
};

export default LinkWirePage;
